import { useState, useEffect } from 'react'
import { X, Save, AlertCircle } from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { SuccessModal } from './SuccessModal'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

interface Plano {
    id: string
    nome: string
    nome_display: string
    preco_mensal: number
}

interface AssinaturaData {
    id: string
    user_id: string
    plano_id: string
    status: string
    data_inicio: string
    data_fim_trial: string | null
    data_proximo_pagamento: string | null
    data_cancelamento: string | null
    valor_mensal: number
    ciclo_pagamento: string
    metodo_pagamento: string | null
    observacoes: string | null
}

interface EditarAssinaturaModalProps {
    isOpen: boolean
    onClose: () => void
    assinaturaId: string
    onSuccess: () => void
}

export function EditarAssinaturaModal({
    isOpen,
    onClose,
    assinaturaId,
    onSuccess
}: EditarAssinaturaModalProps) {
    const [loading, setLoading] = useState(false)
    const [salvando, setSalvando] = useState(false)
    const [planos, setPlanos] = useState<Plano[]>([])
    const [erro, setErro] = useState<string | null>(null)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [formData, setFormData] = useState<AssinaturaData>({
        id: '',
        user_id: '',
        plano_id: '',
        status: 'trial',
        data_inicio: '',
        data_fim_trial: null,
        data_proximo_pagamento: null,
        data_cancelamento: null,
        valor_mensal: 0,
        ciclo_pagamento: 'mensal',
        metodo_pagamento: null,
        observacoes: null
    })

    useEffect(() => {
        if (isOpen && assinaturaId) {
            carregarPlanos()
            carregarAssinatura()
        }
    }, [isOpen, assinaturaId])

    const carregarPlanos = async () => {
        try {
            const { data, error } = await supabase
                .from('planos')
                .select('id, nome, nome_display, preco_mensal')
                .eq('status', 'ativo')
                .order('ordem_exibicao')

            if (error) throw error
            if (data) {
                setPlanos(data)
            }
        } catch (error) {
            console.error('Erro ao carregar planos:', error)
        }
    }

    // Função helper para extrair apenas a data (YYYY-MM-DD) sem considerar fuso horário
    const extractDateOnly = (dateString: string | null): string | null => {
        if (!dateString) return null
        // Se já está no formato YYYY-MM-DD, retorna direto
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString
        }
        // Se está no formato ISO (YYYY-MM-DDTHH:mm:ss...), extrai apenas a parte da data
        const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/)
        return dateMatch ? dateMatch[1] : null
    }

    const carregarAssinatura = async () => {
        setLoading(true)
        setErro(null)
        try {
            const { data, error } = await supabase.rpc('obter_detalhes_assinatura', {
                p_assinatura_id: assinaturaId
            })

            if (error) throw error

            if (data?.sucesso && data.assinatura) {
                const assinatura = data.assinatura
                setFormData({
                    id: assinatura.id,
                    user_id: assinatura.user_id,
                    plano_id: assinatura.plano_id,
                    status: assinatura.status,
                    data_inicio: extractDateOnly(assinatura.data_inicio) || '',
                    data_fim_trial: extractDateOnly(assinatura.data_fim_trial),
                    data_proximo_pagamento: extractDateOnly(assinatura.data_proximo_pagamento),
                    data_cancelamento: extractDateOnly(assinatura.data_cancelamento),
                    valor_mensal: parseFloat(assinatura.valor_mensal) || 0,
                    ciclo_pagamento: assinatura.ciclo_pagamento || 'mensal',
                    metodo_pagamento: assinatura.metodo_pagamento || null,
                    observacoes: assinatura.observacoes || null
                })
            } else {
                setErro(data?.mensagem || 'Erro ao carregar assinatura')
            }
        } catch (error: any) {
            console.error('Erro ao carregar assinatura:', error)
            setErro(error.message || 'Erro ao carregar assinatura')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSalvando(true)
        setErro(null)

        try {
            // Preparar dados para atualização
            const updateData: any = {
                p_assinatura_id: assinaturaId
            }

            // Adicionar apenas campos que foram alterados
            if (formData.plano_id) updateData.p_plano_id = formData.plano_id
            if (formData.status) updateData.p_status = formData.status
            if (formData.data_inicio) {
                updateData.p_data_inicio = new Date(formData.data_inicio).toISOString()
            }
            if (formData.data_fim_trial) {
                updateData.p_data_fim_trial = new Date(formData.data_fim_trial).toISOString()
            } else if (formData.data_fim_trial === '') {
                updateData.p_data_fim_trial = null
            }
            if (formData.data_proximo_pagamento) {
                updateData.p_data_proximo_pagamento = new Date(formData.data_proximo_pagamento).toISOString()
            } else if (formData.data_proximo_pagamento === '') {
                updateData.p_data_proximo_pagamento = null
            }
            if (formData.data_cancelamento) {
                updateData.p_data_cancelamento = new Date(formData.data_cancelamento).toISOString()
            } else if (formData.data_cancelamento === '') {
                updateData.p_data_cancelamento = null
            }
            if (formData.valor_mensal) updateData.p_valor_mensal = formData.valor_mensal
            if (formData.ciclo_pagamento) updateData.p_ciclo_pagamento = formData.ciclo_pagamento
            if (formData.metodo_pagamento !== undefined) {
                updateData.p_metodo_pagamento = formData.metodo_pagamento || null
            }
            if (formData.observacoes !== undefined) {
                updateData.p_observacoes = formData.observacoes || null
            }

            const { data, error } = await supabase.rpc('atualizar_assinatura', updateData)

            if (error) throw error

            if (data?.sucesso) {
                setShowSuccessModal(true)
                // Fechar modal de edição após mostrar sucesso (aguarda o modal de sucesso aparecer)
                setTimeout(() => {
                    onSuccess()
                    onClose()
                }, 2500) // Aguarda o modal de sucesso ser exibido e fechar automaticamente
            } else {
                setErro(data?.mensagem || 'Erro ao atualizar assinatura')
            }
        } catch (error: any) {
            console.error('Erro ao atualizar assinatura:', error)
            setErro(error.message || 'Erro ao atualizar assinatura')
        } finally {
            setSalvando(false)
        }
    }

    const handlePlanoChange = (planoId: string) => {
        const planoSelecionado = planos.find(p => p.id === planoId)
        if (planoSelecionado) {
            setFormData(prev => ({
                ...prev,
                plano_id: planoId,
                valor_mensal: planoSelecionado.preco_mensal
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                plano_id: planoId
            }))
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Editar Assinatura</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
                            <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando dados...</p>
                        </div>
                    ) : erro ? (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-red-800 dark:text-red-300 font-medium">Erro</p>
                                <p className="text-red-600 dark:text-red-400 text-sm mt-1">{erro}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Plano */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Plano *
                                </label>
                                <select
                                    value={formData.plano_id}
                                    onChange={(e) => handlePlanoChange(e.target.value)}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                >
                                    <option value="">Selecione um plano</option>
                                    {planos.map((plano) => (
                                        <option key={plano.id} value={plano.id}>
                                            {plano.nome_display} - R$ {plano.preco_mensal.toFixed(2)}/mês
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Status *
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                >
                                    <option value="trial">Trial</option>
                                    <option value="ativa">Ativa</option>
                                    <option value="cancelada">Cancelada</option>
                                    <option value="expirada">Expirada</option>
                                    <option value="suspensa">Suspensa</option>
                                </select>
                            </div>

                            {/* Datas */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Data de Início *
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.data_inicio}
                                        onChange={(e) => setFormData(prev => ({ ...prev, data_inicio: e.target.value }))}
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Fim do Trial
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.data_fim_trial || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, data_fim_trial: e.target.value || null }))}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Próximo Pagamento
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.data_proximo_pagamento || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, data_proximo_pagamento: e.target.value || null }))}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Data de Cancelamento
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.data_cancelamento || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, data_cancelamento: e.target.value || null }))}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                            </div>

                            {/* Valores e Métodos */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Valor Mensal (R$) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.valor_mensal}
                                        onChange={(e) => setFormData(prev => ({ ...prev, valor_mensal: parseFloat(e.target.value) || 0 }))}
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Ciclo de Pagamento *
                                    </label>
                                    <select
                                        value={formData.ciclo_pagamento}
                                        onChange={(e) => setFormData(prev => ({ ...prev, ciclo_pagamento: e.target.value }))}
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    >
                                        <option value="semanal">Semanal</option>
                                        <option value="mensal">Mensal</option>
                                        <option value="trimestral">Trimestral</option>
                                        <option value="semestral">Semestral</option>
                                        <option value="anual">Anual</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Método de Pagamento
                                    </label>
                                    <select
                                        value={formData.metodo_pagamento || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, metodo_pagamento: e.target.value || null }))}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    >
                                        <option value="">Nenhum</option>
                                        <option value="pix">PIX</option>
                                        <option value="cartao">Cartão de Crédito</option>
                                        <option value="boleto">Boleto</option>
                                    </select>
                                </div>
                            </div>

                            {/* Observações */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Observações
                                </label>
                                <textarea
                                    value={formData.observacoes || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value || null }))}
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                    placeholder="Observações sobre esta assinatura..."
                                />
                            </div>
                        </div>
                    )}
                </form>

                {/* Footer */}
                {!loading && !erro && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-700/50">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors bg-white dark:bg-gray-800"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={salvando}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save className="w-4 h-4" />
                            {salvando ? 'Salvando...' : 'Salvar Alterações'}
                        </button>
                    </div>
                )}
            </div>

            {/* Modal de Sucesso */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Sucesso!"
                message="Assinatura atualizada com sucesso!"
                autoClose={true}
                autoCloseDelay={2000}
            />
        </div>
    )
}

